import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, X, Volume2, Check, Trophy, ThumbsUp, Zap, Target, Flame, HeartCrack, FileText, Clock } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import './QuizComponent.css';
import TribalBorder from '../TribalBorder';
import { LanguageCode } from '../../types/types';
import { cleanParentheses } from '../../utils/cleanParentheses';
import { getWordTranslation } from '../../utils/localizedContent';
import { useSupabaseAudio } from '../../hooks/useSupabaseAudio';

import FloatingBackButton from '../FloatingBackButton';
import { EmojiText } from '../common/Picto';

const LANGUAGE_NAMES: Record<string, string> = {
  wf: 'Wolof', ba: 'Bambara', la: 'Lingala',
  ff: 'Peul', co: 'Copte', sw: 'Swahili', yo: 'Yoruba',
};

const TOTAL_QUESTIONS = 15;

interface Word {
  original: string;
  translation: string;
  translationEn?: string;
}

interface QuizOption {
  original: string;    // mot en langue cible
  translation: string; // traduction française
}

interface QuizQuestion {
  question: string;      // mot français à traduire
  options: QuizOption[];
  correctAnswer: string; // mot en langue cible (= correct option.original)
}

interface QuizComponentProps {
  languageCode: LanguageCode;
  words: Record<string, Word[]>;
  onComplete: (score: number) => void;
  onAnswerSubmit: (correct: boolean) => void;
  backgroundAudioUrl: string;
  correctAnswerSoundUrl: string;
  wrongAnswerSoundUrl: string;
  onGameFinished: () => void;
  isMobileView?: boolean;
}

const QuizComponent: React.FC<QuizComponentProps> = ({
  languageCode,
  words,
  onComplete,
  onAnswerSubmit,
  backgroundAudioUrl,
  correctAnswerSoundUrl,
  wrongAnswerSoundUrl,
  onGameFinished,
  isMobileView = false
}) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();

  // ✅ AJOUT: Utilisation du hook useSupabaseAudio pour la lecture audio
  const { playWord } = useSupabaseAudio(languageCode);
  
  // Détection automatique de la vue mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  const effectiveIsMobileView = isMobileView || isMobile;
  
  // États du quiz
  const [quizQuestion, setQuizQuestion] = useState<QuizQuestion | null>(null);
  const [nextQuestion, setNextQuestion] = useState<QuizQuestion | null>(null);
  const [quizScore, setQuizScore] = useState(0);
  const [timer, setTimer] = useState(10);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [streak, setStreak] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [bestStreak, setBestStreak] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [questionKey, setQuestionKey] = useState(0);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [finalScore, setFinalScore] = useState(0);

  const backgroundAudioRef = useRef<HTMLAudioElement | null>(null);
  const correctAnswerAudioRef = useRef<HTMLAudioElement | null>(null);
  const wrongAnswerAudioRef = useRef<HTMLAudioElement | null>(null);
  const lifeLostRef = useRef(false);
  const nextQuestionTimeoutRef = useRef<NodeJS.Timeout>();
  const timerIntervalRef = useRef<NodeJS.Timeout>();

  // Initialisation audio
  useEffect(() => {
    backgroundAudioRef.current = new Audio(backgroundAudioUrl);
    correctAnswerAudioRef.current = new Audio(correctAnswerSoundUrl);
    wrongAnswerAudioRef.current = new Audio(wrongAnswerSoundUrl);
    
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.loop = true;
      backgroundAudioRef.current.volume = 0.5;
    }
    
    if (correctAnswerAudioRef.current) {
      correctAnswerAudioRef.current.volume = 0.7;
    }
    
    if (wrongAnswerAudioRef.current) {
      wrongAnswerAudioRef.current.volume = 0.7;
    }
    
    return () => {
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current.pause();
        backgroundAudioRef.current = null;
      }
      if (correctAnswerAudioRef.current) {
        correctAnswerAudioRef.current.pause();
        correctAnswerAudioRef.current = null;
      }
      if (wrongAnswerAudioRef.current) {
        wrongAnswerAudioRef.current.pause();
        wrongAnswerAudioRef.current = null;
      }
    };
  }, [backgroundAudioUrl, correctAnswerSoundUrl, wrongAnswerSoundUrl]);


  const allWords = useMemo(() => {
    const flatWords = Object.values(words).flat();
    const filteredWords = flatWords.filter((word, index) => {
      if (!word) {
        return false;
      }
      
      if (!word.original || !word.translation) {
        return false;
      }
      
      if (typeof word.original !== 'string' || typeof word.translation !== 'string') {
        return false;
      }
      
      if (word.original.includes('_category') || word.translation.includes('_category')) {
        return false;
      }
      
      if (word.original.trim() === '' || word.translation.trim() === '') {
        return false;
      }
      
      return true;
    });
    
    return filteredWords;
  }, [words]);

  // Joue la prononciation du mot en langue cible (la bonne réponse)
  const playQuestionAudio = useCallback(async () => {
    if (!quizQuestion || isPlayingAudio) return;
    try {
      setIsPlayingAudio(true);
      await playWord(quizQuestion.correctAnswer);
    } catch (error) {
    } finally {
      setIsPlayingAudio(false);
    }
  }, [quizQuestion, playWord, isPlayingAudio]);

  // Génération de question avec logs détaillés
  const generateQuizQuestion = useCallback((preload = false) => {
    if (allWords.length < 4) {
      return null;
    }
    
    // Sélectionner le mot correct
    const correctWordIndex = Math.floor(Math.random() * allWords.length);
    const correctWord = allWords[correctWordIndex];

    // Générer les options incorrectes (direction : français → langue cible)
    const usedOriginals = new Set([correctWord.original]);
    const options: QuizOption[] = [
      { original: correctWord.original, translation: getWordTranslation(correctWord, i18n.language) }
    ];

    let attempts = 0;
    const maxAttempts = 200;

    while (options.length < 4 && attempts < maxAttempts) {
      const randomIndex = Math.floor(Math.random() * allWords.length);
      const randomWord = allWords[randomIndex];

      if (randomWord &&
          randomWord.original &&
          typeof randomWord.original === 'string' &&
          randomWord.original.trim() !== '' &&
          !usedOriginals.has(randomWord.original)) {
        options.push({ original: randomWord.original, translation: getWordTranslation(randomWord, i18n.language) });
        usedOriginals.add(randomWord.original);
      }

      attempts++;
    }

    if (options.length < 4) {
      return null;
    }

    // Mélanger les options
    const shuffledOptions = [...options];
    for (let i = shuffledOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledOptions[i], shuffledOptions[j]] = [shuffledOptions[j], shuffledOptions[i]];
    }

    const newQuestion: QuizQuestion = {
      question: getWordTranslation(correctWord, i18n.language),  // affiché — matching se fait sur .original, pas ce champ
      options: shuffledOptions,
      correctAnswer: correctWord.original, // l'utilisateur choisit le mot cible
    };

    if (preload) {
      setNextQuestion(newQuestion);
      return newQuestion;
    }
    
    setIsLoadingQuestion(true);
    
    setTimeout(() => {
      setQuizQuestion(newQuestion);
      setQuestionKey(prev => prev + 1);
      setTimer(10);
      setSelectedAnswer(null);
      setShowAnswer(false);
      lifeLostRef.current = false;
      setIsLoadingQuestion(false);
      
      // Précharger la prochaine question
      setTimeout(() => {
        generateQuizQuestion(true);
      }, 1000);
      
      // Jouer l'audio de fond
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current.currentTime = 0;
        backgroundAudioRef.current.play().catch(error => {
});
      }
    }, 50);
    
    return newQuestion;
  }, [allWords, i18n.language]);

  // Transition vers la prochaine question
  const moveToNextQuestion = useCallback(() => {
    setIsTransitioning(true);
    
    setTimeout(() => {
      if (nextQuestion) {
        setQuizQuestion(nextQuestion);
        setNextQuestion(null);
        setQuestionKey(prev => prev + 1);
        generateQuizQuestion(true);
      } else {
        generateQuizQuestion();
      }
      
      setTimer(10);
      setSelectedAnswer(null);
      setShowAnswer(false);
      setIsTransitioning(false);
      lifeLostRef.current = false;
      
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current.currentTime = 0;
        backgroundAudioRef.current.play().catch(error => {
});
      }
    }, 300);
  }, [nextQuestion, generateQuizQuestion]);

  const handleQuizAnswer = useCallback(
    (answer: string) => {
      if (!quizQuestion || showAnswer || isTransitioning) return;

      clearTimeout(nextQuestionTimeoutRef.current);
      clearInterval(timerIntervalRef.current);
      
      setSelectedAnswer(answer);
      setShowAnswer(true);
      
      if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
        backgroundAudioRef.current.pause();
      }

      const isCorrect = answer === quizQuestion.correctAnswer;
      const isTimeout = answer === '';
      
      onAnswerSubmit(isCorrect);
      const newAnsweredCount = answeredCount + 1;
      setAnsweredCount(newAnsweredCount);

      if (isCorrect) {
        setQuizScore(prev => prev + 1);
        setStreak(prev => {
          const newStreak = prev + 1;
          setBestStreak(current => Math.max(current, newStreak));
          return newStreak;
        });
        if (correctAnswerAudioRef.current) {
          correctAnswerAudioRef.current.currentTime = 0;
          correctAnswerAudioRef.current.play().catch(() => {});
        }
      } else {
        if (!lifeLostRef.current) {
          setLives(prev => prev - 1);
          lifeLostRef.current = true;
        }
        setStreak(0);
        if (!isTimeout && wrongAnswerAudioRef.current) {
          wrongAnswerAudioRef.current.currentTime = 0;
          wrongAnswerAudioRef.current.play().catch(() => {});
        }
      }

      nextQuestionTimeoutRef.current = setTimeout(() => {
        if (newAnsweredCount >= TOTAL_QUESTIONS) {
          const score = quizScore + (isCorrect ? 1 : 0);
          setFinalScore(score);
          setShowSummary(true);
          onComplete(score);
          return;
        }
        if (lives > 1 || (lives === 1 && isCorrect)) {
          moveToNextQuestion();
        } else {
          setGameOver(true);
        }
      }, 1200);
    },
    [quizQuestion, showAnswer, lives, onAnswerSubmit, moveToNextQuestion, isTransitioning, quizScore, answeredCount]
  );

  const handleStartQuiz = useCallback(() => {
    setGameStarted(true);
    setIsLoadingQuestion(true);
    
    setTimeout(() => {
      generateQuizQuestion();
    }, 100);
  }, [generateQuizQuestion, allWords.length]);

  const handleBackToMenu = useCallback(() => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
    }
    if (correctAnswerAudioRef.current) {
      correctAnswerAudioRef.current.pause();
    }
    if (wrongAnswerAudioRef.current) {
      wrongAnswerAudioRef.current.pause();
    }
    
    clearTimeout(nextQuestionTimeoutRef.current);
    clearInterval(timerIntervalRef.current);
    
    onGameFinished();
  }, [onGameFinished]);

  // Clic direct sur une option = réponse immédiate
  const handleSelectOption = useCallback((original: string) => {
    if (showAnswer || isTransitioning) return;
    handleQuizAnswer(original);
  }, [showAnswer, isTransitioning, handleQuizAnswer]);

  // Nettoyage
  useEffect(() => {
    return () => {
      clearTimeout(nextQuestionTimeoutRef.current);
      clearInterval(timerIntervalRef.current);
    };
  }, []);

  // Countdown timer — reset on new question, timeout = wrong answer
  useEffect(() => {
    if (!gameStarted || !quizQuestion || showAnswer || isTransitioning) return;
    setTimer(10);
    clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          handleQuizAnswer('');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerIntervalRef.current);
  }, [questionKey, gameStarted]);

  const handleRetry = useCallback(() => {
    setQuizScore(0);
    setLives(3);
    setGameOver(false);
    setShowSummary(false);
    setFinalScore(0);
    setStreak(0);
    setBestStreak(0);
    setTimer(10);
    setAnsweredCount(0);
    setGameStarted(true);
    setIsTransitioning(false);
    setQuestionKey(0);
    setNextQuestion(null);
    setQuizQuestion(null);
    setIsLoadingQuestion(true);
    
    setTimeout(() => {
      generateQuizQuestion();
    }, 100);
  }, [generateQuizQuestion]);

  // Animations
  const containerVariants = {
    initial: { opacity: 0, scale: 0.9, y: 20 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.9, y: -20 }
  };

  const questionVariants = {
    initial: { opacity: 0, x: 30, scale: 0.95 },
    animate: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: -30, scale: 0.95 }
  };

  const optionVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    hover: { scale: 1.02, y: -2 },
    tap: { scale: 0.98 }
  };

  // Écran de résumé (quiz terminé)
  if (showSummary) {
    const pct = Math.round((finalScore / TOTAL_QUESTIONS) * 100);
    const ResultIcon = pct >= 80 ? Trophy : pct >= 50 ? ThumbsUp : Zap;
    const msg = pct >= 80 ? t('quiz.resultExcellent') : pct >= 50 ? t('quiz.resultGood') : t('quiz.resultKeepPracticing');
    return (
      <div className="qz-splash" data-theme={theme}>
        <TribalBorder />
        <FloatingBackButton onBack={handleBackToMenu} position="top-left" variant="minimal" />
        <div className="qz-splash__arch">
          <span className="qz-splash__deco">◇ · ◇</span>
          <div className="qz-splash__icon"><ResultIcon size={40} strokeWidth={1.5} /></div>
          <h2 className="qz-splash__title">{msg}</h2>
          <p className="qz-splash__sub">{t('quiz.finished')}</p>
          <div className="qz-splash__score">{finalScore} <span>/ {TOTAL_QUESTIONS}</span></div>
        </div>
        <div className="qz-splash__info">
          <div className="qz-splash__info-row"><span><Target size={15} strokeWidth={1.75} /></span><span>{t('quiz.successRate', { pct })}</span></div>
          <div className="qz-splash__info-row"><span><Flame size={15} strokeWidth={1.75} /></span><span>{t('quiz.bestStreak', { count: bestStreak })}</span></div>
        </div>
        <motion.button
          className="qz-splash__btn"
          onClick={handleRetry}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          whileTap={{ scale: 0.97 }}
        >
          <EmojiText text={t('quiz.replay') as string} />
        </motion.button>
        <FloatingBackButton onBack={handleBackToMenu} mode="inline" variant="ghost" text={t('quiz.back') as string} />
      </div>
    );
  }

  // Écran Game Over
  if (gameOver) {
    return (
      <div className="qz-splash" data-theme={theme}>
        <TribalBorder />
        <FloatingBackButton onBack={handleBackToMenu} position="top-left" variant="minimal" />
        <div className="qz-splash__arch">
          <span className="qz-splash__deco">◇ · ◇</span>
          <div className="qz-splash__icon"><HeartCrack size={40} strokeWidth={1.5} /></div>
          <h2 className="qz-splash__title">{t('quiz.gameOver')}</h2>
          <p className="qz-splash__sub">{t('quiz.finalScore')}</p>
          <div className="qz-splash__score">{quizScore} <span>/ {TOTAL_QUESTIONS}</span></div>
        </div>
        <motion.button
          className="qz-splash__btn"
          onClick={handleRetry}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          whileTap={{ scale: 0.97 }}
        >
          <EmojiText text={t('quiz.retry') as string} />
        </motion.button>
        <FloatingBackButton onBack={handleBackToMenu} mode="inline" variant="ghost" text={t('quiz.back') as string} />
      </div>
    );
  }

  // Écran de démarrage
  if (!gameStarted) {
    return (
      <div className="qz-splash" data-theme={theme}>
        <TribalBorder />
        <FloatingBackButton onBack={handleBackToMenu} position="top-left" variant="minimal" />
        <div className="qz-splash__arch">
          <span className="qz-splash__deco">◇ · ◇</span>
          <div className="qz-splash__icon"><Trophy size={40} strokeWidth={1.5} /></div>
          <h2 className="qz-splash__title">{t('quiz.title')}</h2>
          <p className="qz-splash__sub">{LANGUAGE_NAMES[languageCode] ?? languageCode}</p>
        </div>
        <div className="qz-splash__info">
          <div className="qz-splash__info-row"><span><Heart size={15} strokeWidth={1.75} /></span><span>{t('quiz.lives')}</span></div>
          <div className="qz-splash__info-row"><span><Clock size={15} strokeWidth={1.75} /></span><span>{t('quiz.secondsPerQuestion')}</span></div>
          <div className="qz-splash__info-row"><span><FileText size={15} strokeWidth={1.75} /></span><span>{t('quiz.questionsCount', { count: TOTAL_QUESTIONS })}</span></div>
        </div>
        <motion.button
          className="qz-splash__btn"
          onClick={handleStartQuiz}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileTap={{ scale: 0.97 }}
        >
          {t('quiz.start')}
        </motion.button>
      </div>
    );
  }

  // Interface de jeu principale — Hi-Fi
  return (
    <div className="quiz-hifi-wrapper" data-theme={theme}>

      {/* ── Barre supérieure ── */}
      <div className="quiz-topbar">
        <motion.button
          className="quiz-close-btn"
          onClick={handleBackToMenu}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label={t('quiz.quitAria') as string}
        >
          <X size={20} />
        </motion.button>

        <div className="quiz-segments" role="progressbar" aria-valuenow={answeredCount} aria-valuemax={TOTAL_QUESTIONS}>
          {[...Array(TOTAL_QUESTIONS)].map((_, i) => (
            <div key={i} className={`quiz-segment${i < answeredCount ? ' filled' : ''}`} />
          ))}
        </div>

        <div className="quiz-lives-row">
          <Heart fill="#e74c3c" color="#e74c3c" size={16} />
          <span>{lives}</span>
        </div>
      </div>

      {/* ── Chrono ── */}
      <div className="quiz-timer-row">
        <div className="quiz-timer-track">
          <motion.div
            className="quiz-timer-fill"
            animate={{ width: `${(timer / 10) * 100}%` }}
            transition={{ duration: 0.9, ease: 'linear' }}
            style={{ background: timer <= 3 ? 'var(--color-error)' : 'var(--color-accent, var(--gold-primary))' }}
          />
        </div>
        <span className="quiz-timer-count" style={{ color: timer <= 3 ? 'var(--color-error)' : 'var(--color-accent, var(--gold-primary))' }}>
          {timer}
        </span>
      </div>

      {/* ── Contenu ── */}
      <div className="quiz-game-content">
        {quizQuestion && !isLoadingQuestion ? (
          <motion.div
            key={questionKey}
            variants={questionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.35, ease: 'easeInOut' }}
          >
            {/* Carte mot */}
            <div className="quiz-word-card">
              <div className="quiz-card-deco-top" aria-hidden="true" />
              <div className="quiz-card-inner">
                <div className="quiz-arch-frame">
                  <p className="quiz-lang-label">
                    {t('quiz.translateInto', { lang: LANGUAGE_NAMES[languageCode] ?? languageCode.toUpperCase() })}
                  </p>
                  <h2 className="quiz-word-display">
                    {cleanParentheses(quizQuestion.question)}
                  </h2>
                </div>
              </div>
              <div className="quiz-card-deco-bottom" aria-hidden="true" />
            </div>

            {/* Grille options */}
            <div className="quiz-options-hifi">
              {quizQuestion.options.map((option, index) => {
                const isSelected = !showAnswer && selectedAnswer === option.original;
                const isCorrect  = showAnswer && option.original === quizQuestion.correctAnswer;
                const isWrong    = showAnswer && selectedAnswer === option.original && option.original !== quizQuestion.correctAnswer;

                return (
                  <motion.button
                    key={`${questionKey}-${index}-${option.original}`}
                    className={`quiz-option-hifi${isSelected ? ' selected' : ''}${isCorrect ? ' correct' : ''}${isWrong ? ' wrong' : ''}`}
                    onClick={() => handleSelectOption(option.original)}
                    disabled={showAnswer || isTransitioning}
                    variants={optionVariants}
                    initial="initial"
                    animate="animate"
                    whileHover={!showAnswer && !isTransitioning ? 'hover' : {}}
                    whileTap={!showAnswer && !isTransitioning ? 'tap' : {}}
                    transition={{ delay: 0.08 + index * 0.05 }}
                  >
                    <span className="quiz-option-word">{cleanParentheses(option.original)}</span>
                    {showAnswer && <span className="quiz-option-translation">{option.translation}</span>}
                    {(isCorrect || isWrong) && (
                      <span className="quiz-option-state-icon">{isCorrect ? <Check size={16} strokeWidth={3} /> : <X size={16} strokeWidth={3} />}</span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        ) : (
          <div className="quiz-loading-state">
            {isLoadingQuestion ? t('quiz.loading') : t('quiz.preparing')}
          </div>
        )}
      </div>

    </div>
  );
};

export default QuizComponent;
